import { useEffect, useState, useCallback, useRef } from "react";
import { router } from "@inertiajs/react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import posthog from "posthog-js";
import { Check, Coins, Copy, Loader2, QrCode } from "lucide-react";

interface Product {
  id: number;
  paid_credits: number;
  priceFormatted: string;
}

interface PixData {
  paymentId: number;
  brCode: string;
  qrCodeUrl: string;
  expiresAt: string;
}

interface Props {
  product: Product;
}

export default function PixCheckout({ product }: Props) {
  const [pixData, setPixData] = useState<PixData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState<"pending" | "completed">("pending");
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    posthog.capture("checkout_started", { product_id: product.id, payment_method: "pix" });
  }, [product.id]);

  const createPixSession = useCallback(async () => {
    try {
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

      const res = await fetch(`/paid_credits/create_pix_session?product_id=${product.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Erro ao gerar cobrança PIX.");
        return;
      }

      setPixData(data);
    } catch {
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => {
    createPixSession();
  }, [createPixSession]);

  // Poll for payment completion
  useEffect(() => {
    if (!pixData || status === "completed") return;

    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/paid_credits/pix_status?payment_id=${pixData.paymentId}`);
        const data = await res.json();

        if (data.status === "completed") {
          setStatus("completed");
          if (pollingRef.current) clearInterval(pollingRef.current);
          setTimeout(() => {
            router.visit("/paid_credits", {
              preserveState: false,
            });
          }, 2000);
        }
      } catch {
        // Silently retry on next interval
      }
    }, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [pixData, status]);

  const handleCopy = async () => {
    if (!pixData) return;
    await navigator.clipboard.writeText(pixData.brCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (status === "completed") {
    return (
      <Layout title="Pagamento">
        <div className="mx-auto max-w-lg px-4 py-20 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/20">
            <Check className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-semibold">Pagamento confirmado!</h1>
          <p className="mt-2 text-muted-foreground">
            {product.paid_credits} créditos foram adicionados à sua conta.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">Redirecionando...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Pagamento">
      <div className="mx-auto max-w-lg px-4 py-10">
        <header className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20">
            <Coins className="h-8 w-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Pagar com PIX</h1>
          <p className="mt-2 text-muted-foreground">
            {product.paid_credits} créditos por {product.priceFormatted}
          </p>
        </header>

        {loading && (
          <div className="flex flex-col items-center gap-4 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Gerando cobrança PIX...</p>
          </div>
        )}

        {error && (
          <Card className="border-red-500/20 bg-red-500/10">
            <CardContent className="py-6 text-center">
              <p className="text-red-400">{error}</p>
              <Button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  createPixSession();
                }}
                variant="outline"
                className="mt-4"
              >
                Tentar novamente
              </Button>
            </CardContent>
          </Card>
        )}

        {pixData && !error && (
          <div className="space-y-6">
            <Card className="border-border bg-card">
              <CardContent className="flex flex-col items-center gap-6 py-8">
                {pixData.qrCodeUrl ? (
                  <div className="rounded-xl bg-white p-4">
                    <img src={pixData.qrCodeUrl} alt="QR Code PIX" className="w-full max-w-52" />
                  </div>
                ) : (
                  <div className="flex w-full max-w-52 aspect-square items-center justify-center rounded-xl bg-muted">
                    <QrCode className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}

                <div className="w-full space-y-3">
                  <p className="text-center text-sm text-muted-foreground">
                    Escaneie o QR Code ou copie o código abaixo
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={pixData.brCode}
                      className="flex-1 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground outline-none"
                    />
                    <Button onClick={handleCopy} variant="outline" className="shrink-0 gap-2">
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 text-emerald-400" />
                          Copiado
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copiar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Aguardando pagamento...</span>
            </div>

            {pixData.expiresAt && (
              <p className="text-center text-xs text-muted-foreground">
                Este código expira em{" "}
                {new Date(pixData.expiresAt).toLocaleString("pt-BR", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
